import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { usePageContext } from './PageContext';
import { useFeatureFlag } from './FeaturesContext';
import { useLocation } from 'react-router-dom';
import type {
  Thread,
  ThreadMessage,
  ThreadFile,
  ThreadContextData,
  AgentProcessingStartedEvent,
  AgentProcessingStoppedEvent,
  AgentMessageReceivedEvent,
  AgentCacheInvalidateEvent,
  EnumValue,
  GetThreadsParams,
} from 'lib.store/src/type-partner/ai-agent/models';
import {
  useGetThreadsQuery,
  useLazyGetThreadByIdQuery,
  useCreateThreadMutation,
  useSendMessageMutation,
  useDeleteThreadMutation,
  useStopThreadMutation,
  aiAgentApi,
} from 'lib.store/src/type-partner/ai-agent/ai-agent.api';
import { useDispatch, useStore } from 'react-redux';
import { message } from 'antd';
import { showApiError } from 'lib.shared/src/handleApiError';
import { useThreadMessages } from 'lib.hooks/src/useThreadMessages';
import { getActiveTags } from 'lib.store/src/utils/getActiveTags';
import { ORGANIZATION_TYPE } from 'lib.store/src/main/organization/organizationType';
import { invalidateTagsByName } from 'lib.store/src/utils/invalidateTagsByName';
import type { RootState, AppDispatch } from 'lib.store/src';

const playNotificationSound = () => {
  try {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    oscillator.frequency.setValueAtTime(1047, ctx.currentTime + 0.08);

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.2);
  } catch {
    // Audio not available
  }
};

type SidebarView = 'threads' | 'chat';

interface TypingState {
  isTyping: boolean;
  agentType: EnumValue | null;
}

export interface CreateNewChatOptions {
  agentType?: string;
  extraContext?: Partial<ThreadContextData>;
}

interface AiAssistantContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggleOpen: () => void;
  view: SidebarView;
  setView: (view: SidebarView) => void;
  threads: Thread[];
  isThreadsLoading: boolean;
  isThreadsFetching: boolean;
  isLastPage: boolean;
  loadMoreThreads: () => void;
  currentThread: Thread | null;
  messages: ThreadMessage[];
  /** the open thread's first page of messages is still in flight */
  isMessagesLoading: boolean;
  /** changes when the conversation grows at the bottom, not when older pages prepend */
  messagesTailKey: string;
  /** earlier messages exist above the ones held — the chat offers to walk up */
  hasOlderMessages: boolean;
  isLoadingOlderMessages: boolean;
  loadOlderMessages: (container?: HTMLElement | null) => void;
  typingState: TypingState;
  openThread: (threadId: number) => void;
  createNewChat: (message?: string, options?: CreateNewChatOptions) => void;
  sendMessage: (message: string, files?: ThreadFile[]) => void;
  isSending: boolean;
  stopAgent: () => void;
  isStopping: boolean;
  goBackToThreads: () => void;
  deleteThread: (threadId: number) => void;
  unreadCount: number;
  unreadByThread: Record<number, number>;
  selectedAgentType: string | undefined;
  setSelectedAgentType: (agentType: string | undefined) => void;
}

export const AiAssistantContext = createContext<AiAssistantContextType | undefined>(undefined);

interface AiAssistantProviderProps {
  children: ReactNode;
}

export const AiAssistantProvider: React.FC<AiAssistantProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadByThread, setUnreadByThread] = useState<Record<number, number>>({});
  const [view, setView] = useState<SidebarView>('threads');

  // Sync body data attribute so CSS can offset drawers when AI sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.dataset.aiSidebarOpen = 'true';
    } else {
      delete document.body.dataset.aiSidebarOpen;
    }
    return () => {
      delete document.body.dataset.aiSidebarOpen;
    };
  }, [isOpen]);

  // Expose AI sidebar width as CSS variable on body for drawer offset
  useEffect(() => {
    if (!isOpen) return;
    const sidebar = document.querySelector('.ai-assistant-sidebar') as HTMLElement | null;
    if (!sidebar) return;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      document.body.style.setProperty('--ai-sidebar-width', `${width}px`);
    });
    observer.observe(sidebar);

    return () => {
      observer.disconnect();
      document.body.style.removeProperty('--ai-sidebar-width');
    };
  }, [isOpen]);
  const [currentThread, setCurrentThread] = useState<Thread | null>(null);

  // the open thread's conversation, page by page. The seed is the newly created
  // thread's own message, so a just-started chat shows it without a round trip
  const {
    messages,
    append: appendMessage,
    isLoading: isMessagesLoading,
    hasOlder: hasOlderMessages,
    isLoadingOlder: isLoadingOlderMessages,
    loadOlder: loadOlderMessages,
    tailKey: messagesTailKey,
    remove: removeMessage,
  } = useThreadMessages(currentThread?.id ?? null, currentThread?.messages);
  const [typingState, setTypingState] = useState<TypingState>({
    isTyping: false,
    agentType: null,
  });

  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const { filters, entityContext } = usePageContext();
  const dispatch = useDispatch();
  const reduxStore = useStore<RootState>();
  const organizationId = user?.organization?.id || '';
  const isAttendee = user?.organization?.type === ORGANIZATION_TYPE.attendee;
  const isAiAgentEnabled = useFeatureFlag('ai_agent');
  const currentThreadRef = useRef<Thread | null>(null);
  // bumped on every typing-state broadcast; a failed stop restores its
  // snapshot only when no fresher event has landed in between
  const typingEventsVersionRef = useRef(0);
  const isOpenRef = useRef(isOpen);
  const viewRef = useRef<SidebarView>(view);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const [threadsPage, setThreadsPage] = useState(1);
  const [selectedAgentType, setSelectedAgentTypeState] = useState<string | undefined>(undefined);

  const setSelectedAgentType = useCallback((agentType: string | undefined) => {
    setSelectedAgentTypeState(agentType);
    setThreadsPage(1);
  }, []);

  const {
    data: threadsData,
    isLoading: isThreadsLoading,
    isFetching: isThreadsFetching,
  } = useGetThreadsQuery(
    { page: threadsPage, agent_type: selectedAgentType },
    {
      skip: !isAuthenticated || !isAiAgentEnabled,
    },
  );

  const isLastPage = threadsData
    ? threadsData.meta.current_page >= threadsData.meta.last_page
    : true;

  const loadMoreThreads = useCallback(() => {
    if (!isThreadsFetching && !isLastPage) {
      setThreadsPage((prev) => prev + 1);
    }
  }, [isThreadsFetching, isLastPage]);

  const [getThreadById] = useLazyGetThreadByIdQuery();
  const [createThreadMutation] = useCreateThreadMutation();
  const [sendMessageMutation, { isLoading: isSending }] = useSendMessageMutation();
  const [deleteThreadMutation] = useDeleteThreadMutation();
  const [stopThreadMutation, { isLoading: isStopping }] = useStopThreadMutation();

  const threads = threadsData?.data || [];

  useEffect(() => {
    currentThreadRef.current = currentThread;
  }, [currentThread]);

  // WebSocket subscription
  useEffect(() => {
    // attendees never see the floating widget — their chats live on full
    // pages tracked by AgentUnreadContext; counting (and beeping) here on
    // top of that would double every concierge reply
    if (!organizationId || !window.Echo || !isAiAgentEnabled || isAttendee) return;

    const channel = window.Echo.private(`ai.agent.organization.${organizationId}`);

    const handleProcessingStarted = (event: AgentProcessingStartedEvent) => {
      const thread = currentThreadRef.current;
      if (thread && event.thread_id === thread.id) {
        typingEventsVersionRef.current += 1;
        setTypingState({
          isTyping: true,
          agentType: event.agent_type,
        });
      }
    };

    const handleProcessingStopped = (event: AgentProcessingStoppedEvent) => {
      const thread = currentThreadRef.current;
      if (thread && event.thread_id === thread.id) {
        typingEventsVersionRef.current += 1;
        setTypingState({ isTyping: false, agentType: null });
      }
    };

    const handleMessageReceived = (event: AgentMessageReceivedEvent) => {
      const thread = currentThreadRef.current;
      const isViewingThread =
        isOpenRef.current && viewRef.current === 'chat' && thread && event.thread_id === thread.id;

      if (isViewingThread) {
        appendMessage(event.message);
        typingEventsVersionRef.current += 1;
        setTypingState({ isTyping: false, agentType: null });
      } else {
        // Track unread per thread when user is not viewing this thread
        setUnreadByThread((prev) => ({
          ...prev,
          [event.thread_id]: (prev[event.thread_id] || 0) + 1,
        }));
      }
      if (!isOpenRef.current) {
        setUnreadCount((prev) => prev + 1);
        playNotificationSound();
      }
      // Reset to first page and refetch threads list
      setThreadsPage(1);
      dispatch(aiAgentApi.util.invalidateTags([{ type: 'ai_agent', id: 'LIST' }]));
    };

    const handleCacheInvalidate = (event: AgentCacheInvalidateEvent) => {
      if (event.tags?.length) {
        invalidateTagsByName(dispatch as AppDispatch, event.tags);
      }
    };

    channel
      .listen('.agent.processing.started', handleProcessingStarted)
      .listen('.agent.processing.stopped', handleProcessingStopped)
      .listen('.agent.message.received', handleMessageReceived)
      .listen('.agent.cache.invalidate', handleCacheInvalidate);

    return () => {
      // targeted removal — other surfaces (AgentUnreadContext, agent chat
      // pages) listen to the same events on this shared channel
      channel.stopListening('.agent.processing.started', handleProcessingStarted);
      channel.stopListening('.agent.processing.stopped', handleProcessingStopped);
      channel.stopListening('.agent.message.received', handleMessageReceived);
      channel.stopListening('.agent.cache.invalidate', handleCacheInvalidate);
    };
  }, [organizationId, isAiAgentEnabled, isAttendee, dispatch, appendMessage]);

  const getPageContext = useCallback((): ThreadContextData => {
    const context: ThreadContextData = {
      page: location.pathname,
    };

    if (filters.eventIds.length > 0) {
      context.event_ids = filters.eventIds.map(Number);
    }
    if (entityContext.campaignId) {
      context.campaign_ids = [entityContext.campaignId];
    }
    if (entityContext.landingId) {
      context.landing_ids = [entityContext.landingId];
    }
    if (entityContext.eventId) {
      context.event_ids = [...(context.event_ids || []), entityContext.eventId];
    }

    const activeTags = getActiveTags(reduxStore.getState());
    if (activeTags.length > 0) {
      context.active_tags = activeTags;
    }

    return context;
  }, [entityContext, filters.eventIds, location.pathname, reduxStore]);

  const openThread = useCallback(
    async (threadId: number) => {
      const result = await getThreadById({ id: threadId });
      if (result.data?.data) {
        // the thread; its newest page of messages follows from the hook, and
        // earlier ones are walked from the chat view — opening a months-old
        // thread costs one page
        setCurrentThread(result.data.data);
        setView('chat');
        setTypingState({ isTyping: false, agentType: null });
        // Clear unread count for this thread
        setUnreadByThread((prev) => {
          if (!prev[threadId]) return prev;
          const next = { ...prev };
          delete next[threadId];
          return next;
        });
      }
    },
    [getThreadById],
  );

  const createNewChat = useCallback(
    async (message?: string, options?: CreateNewChatOptions) => {
      const baseContext = getPageContext();
      const context = options?.extraContext
        ? { ...baseContext, ...options.extraContext }
        : baseContext;

      const agentType = options?.agentType || selectedAgentType;

      if (options?.agentType && options.agentType !== selectedAgentType) {
        setSelectedAgentType(options.agentType);
      }

      const result = await createThreadMutation({
        message,
        context,
        agent_type: agentType,
      });

      if ('data' in result && result.data?.data) {
        const thread = result.data.data;
        setCurrentThread(thread);
        setView('chat');
        if (message) {
          setTypingState({
            isTyping: true,
            agentType: { id: 'orchestrator', name: 'orchestrator', translate: 'Orchestrator' },
          });
        }
      }
    },
    [createThreadMutation, getPageContext, selectedAgentType, setSelectedAgentType],
  );

  const handleSendMessage = useCallback(
    async (message: string, files?: ThreadFile[]) => {
      if (!currentThread) return;

      // Optimistic update — add user message immediately
      const optimisticMessage: ThreadMessage = {
        id: Date.now(),
        parent_id: null,
        message,
        is_ai: false,
        agent_type: null,
        role: 'user',
        type: null,
        actions: null,
        artifact: null,
        files: files?.length ? files : undefined,
        created_at: new Date().toISOString(),
      };
      appendMessage(optimisticMessage);

      setTypingState({
        isTyping: true,
        agentType: { id: 'orchestrator', name: 'orchestrator', translate: 'Orchestrator' },
      });

      const context = getPageContext();
      // the conversation this was typed in — the user may open another one
      // before the request comes back
      const threadId = currentThread.id;

      try {
        // the mutation answers with the stored message — swap the bubble for it
        const stored = await sendMessageMutation({
          threadId,
          message,
          context,
          file_ids: files?.map((file) => file.id),
        }).unwrap();

        removeMessage(optimisticMessage.id, threadId);
        appendMessage(stored.data, threadId);
      } catch (err) {
        // `message` here is the text being sent, not antd's — errors go through
        // the shared handler anyway
        removeMessage(optimisticMessage.id, threadId);
        setTypingState({ isTyping: false, agentType: null });
        showApiError(err, 'Failed to send the message', true);
      }
    },
    [currentThread, sendMessageMutation, getPageContext, appendMessage, removeMessage],
  );

  const stopAgent = useCallback(async () => {
    const thread = currentThreadRef.current;
    if (!thread) return;

    // optimistic: the indicator dies immediately, the broadcast confirms it —
    // but keep the previous state so a failed stop restores the control and
    // the user can retry (the agent is still running in that case)
    let previous: TypingState | null = null;
    const versionAtStop = typingEventsVersionRef.current;
    setTypingState((prev) => {
      previous = prev;
      return { isTyping: false, agentType: null };
    });

    try {
      await stopThreadMutation({ id: thread.id }).unwrap();
    } catch (err) {
      // restore only if the user still views the stopped thread AND no
      // broadcast (message received / processing stopped) has updated the
      // state since — restoring over a fresher event would resurrect a
      // typing indicator for a run that already ended
      if (
        previous &&
        currentThreadRef.current?.id === thread.id &&
        typingEventsVersionRef.current === versionAtStop
      ) {
        setTypingState(previous);
      }
      showApiError(err, 'Failed to stop the agent', true);
    }
  }, [stopThreadMutation]);

  const goBackToThreads = useCallback(() => {
    setView('threads');
    setCurrentThread(null);
    setTypingState({ isTyping: false, agentType: null });
  }, []);

  const handleDeleteThread = useCallback(
    async (threadId: number) => {
      try {
        await deleteThreadMutation({ id: threadId }).unwrap();
        // Remove deleted thread from cache without resetting page
        (dispatch as AppDispatch)(
          aiAgentApi.util.updateQueryData(
            'getThreads',
            { agent_type: selectedAgentType } as GetThreadsParams,
            (draft) => {
              draft.data = draft.data.filter((t) => t.id !== threadId);
            },
          ),
        );
        if (currentThread?.id === threadId) {
          goBackToThreads();
        }
      } catch {
        void message.error('Failed to delete conversation');
      }
    },
    [deleteThreadMutation, currentThread, goBackToThreads, dispatch, selectedAgentType],
  );

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) setUnreadCount(0);
      return !prev;
    });
  }, []);

  return (
    <AiAssistantContext.Provider
      value={{
        isOpen,
        setIsOpen,
        toggleOpen,
        view,
        setView,
        threads,
        isThreadsLoading,
        isThreadsFetching,
        isLastPage,
        loadMoreThreads,
        currentThread,
        messages,
        hasOlderMessages,
        isLoadingOlderMessages,
        loadOlderMessages: (container) => void loadOlderMessages(container),
        isMessagesLoading,
        messagesTailKey,
        typingState,
        openThread,
        createNewChat,
        sendMessage: handleSendMessage,
        isSending,
        stopAgent,
        isStopping,
        goBackToThreads,
        deleteThread: handleDeleteThread,
        unreadCount,
        unreadByThread,
        selectedAgentType,
        setSelectedAgentType,
      }}
    >
      {children}
    </AiAssistantContext.Provider>
  );
};

export const useAiAssistant = () => {
  const context = useContext(AiAssistantContext);
  if (!context) {
    throw new Error('useAiAssistant must be used within an AiAssistantProvider');
  }
  return context;
};
